/**
 * AURA Pointer Utilities
 * Shared helpers for normalising mouse and touch events into
 * a single coordinate model, so every feature works on both
 * desktop (mouse) and mobile (touch) without duplication.
 */

export interface PointerCoords {
  clientX: number;
  clientY: number;
}

export type PointerEvent = MouseEvent | TouchEvent;

/**
 * Extract viewport-relative coordinates from a MouseEvent or TouchEvent.
 * Returns null when a TouchEvent carries no active touches (e.g. touchend).
 */
export function getPointerCoords(e: PointerEvent): PointerCoords | null {
  if (e instanceof MouseEvent) {
    return { clientX: e.clientX, clientY: e.clientY };
  }

  if (e instanceof TouchEvent) {
    const touch = e.touches[0] ?? e.changedTouches[0];
    if (!touch) return null;
    return { clientX: touch.clientX, clientY: touch.clientY };
  }

  return null;
}

export type PointerSource = 'mouse' | 'touch';
export type PointerMoveHandler = (
  coords: PointerCoords,
  source: PointerSource,
) => void;

/**
 * Attach both mousemove and touchmove listeners that funnel through
 * a single handler.  Returns a teardown function that removes both.
 */
export function addPointerMoveListeners(
  target: EventTarget,
  handler: PointerMoveHandler,
): () => void {
  const onMouse = (e: Event) => {
    const coords = getPointerCoords(e as MouseEvent);
    if (coords) handler(coords, 'mouse');
  };

  const onTouch = (e: Event) => {
    const coords = getPointerCoords(e as TouchEvent);
    if (coords) handler(coords, 'touch');
  };

  target.addEventListener('mousemove', onMouse);
  target.addEventListener('touchmove', onTouch, { passive: true });

  return () => {
    target.removeEventListener('mousemove', onMouse);
    target.removeEventListener('touchmove', onTouch);
  };
}
